import React, { createContext, useContext, useReducer, useEffect } from 'react';
import supabase from '../services/supabaseClient';
import LocalStorageService from '../services/localStorageService';
import KeyService from '../services/keyService';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return { ...action.payload, isLoggedIn: true, loading: false };
    case 'LOGOUT':
      return { isLoggedIn: false, loading: false };
    case 'REFRESH_TOKEN':
      return { ...state, ...action.payload, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, { isLoggedIn: false, loading: true });

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Wait a bit for Supabase to initialize and potentially restore session from storage
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // First, try to get the current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('AuthContext: Error getting session:', error);
          dispatch({ type: 'LOGOUT' });
          return;
        }

        if (session) {
          // Check if session is expired
          const now = Math.floor(Date.now() / 1000);
          if (session.expires_at && session.expires_at < now) {
            console.log('AuthContext: Session expired, attempting refresh...');
            
            // Try to refresh the session
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError || !refreshData.session) {
              console.error('AuthContext: Failed to refresh expired session:', refreshError);
              dispatch({ type: 'LOGOUT' });
              return;
            }
            
            // Use refreshed session
            const authObject = { 
              user: refreshData.session.user, 
              username: refreshData.session.user.email,
              isLoggedIn: true,
              session: refreshData.session,
              loading: false
            };
            dispatch({ type: 'LOGIN', payload: authObject });
          } else {
            // Session is valid
            const authObject = { 
              user: session.user, 
              username: session.user.email,
              isLoggedIn: true,
              session: session,
              loading: false
            };
            dispatch({ type: 'LOGIN', payload: authObject });
          }
        } else {
          // Clean up any stale app localStorage data (but don't touch Supabase storage)
          const savedAppAuth = localStorage.getItem('encrypt-text-auth');
          if (savedAppAuth) {
            localStorage.removeItem('encrypt-text-auth');
          }
          
          // Also clean legacy auth key if it exists
          const legacyAuth = localStorage.getItem('auth');
          if (legacyAuth) {
            localStorage.removeItem('auth');
          }
          
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        console.error('AuthContext: Error initializing auth:', error);
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          const authObject = {
            user: session.user,
            username: session.user.email,
            isLoggedIn: true,
            session: session,
            loading: false
          };
          dispatch({ type: 'LOGIN', payload: authObject });
          
          // Note: Local keys are NOT automatically synced to cloud on login
          // Users must explicitly click the "Sync All Local Keys" button
          // This preserves user privacy and gives them control over their data
        } else if (event === 'SIGNED_OUT') {
          dispatch({ type: 'LOGOUT' });
        } else if (event === 'TOKEN_REFRESHED' && session) {
          const authObject = {
            user: session.user,
            username: session.user.email,
            isLoggedIn: true,
            session: session,
            loading: false
          };
          dispatch({ type: 'REFRESH_TOKEN', payload: authObject });
        } else if (event === 'PASSWORD_RECOVERY' && session) {
          const authObject = {
            user: session.user,
            username: session.user.email,
            isLoggedIn: true,
            session: session,
            loading: false
          };
          dispatch({ type: 'LOGIN', payload: authObject });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sync with localStorage (but don't interfere with Supabase's own storage)
  useEffect(() => {
    if (state.isLoggedIn && state.user) {
      // Store minimal auth info for our app (don't duplicate Supabase session)
      const appAuthData = {
        isLoggedIn: true,
        username: state.username,
        userEmail: state.user.email,
        userId: state.user.id,
        // Don't store the full session - let Supabase handle that
        lastLogin: new Date().toISOString()
      };
      localStorage.setItem('encrypt-text-auth', JSON.stringify(appAuthData));
    } else {
      localStorage.removeItem('encrypt-text-auth');
    }
  }, [state.isLoggedIn, state.user]);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
